import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Toast from '../components/Toast.jsx'
import Modal from '../components/Modal.jsx'
import BackButton from '../components/BackButton.jsx'
import { useAuth } from '../auth/AuthProvider.jsx'
import { listComprasPendentes, confirmarCompra, moverParaHistoricoCompras, rejeitarItemCompra } from '../data/orders.js'
import { fetchComprasPendentes, updateCompraPendente } from '../data/syncManager.js'
import { canConfirmCompras } from '../utils/permissions.js'
// NOTIFICAÇÕES DESATIVADAS - import { notificarPedidoAprovado, notificarPedidoRecusado } from '../data/pushService.js'

function formatDate(ts) {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleDateString('pt-BR')
  } catch {
    return '—'
  }
}

export default function ComprasPage() {
  const { session } = useAuth()
  const navigate = useNavigate()
  const perfil = session?.perfil
  
  const [pedidos, setPedidos] = useState([])
  const [toast, setToast] = useState(null)
  const [toastType, setToastType] = useState('info')
  const [busy, setBusy] = useState(false)
  
  // Modal de confirmação
  const [confirmModal, setConfirmModal] = useState({ open: false, pedidoId: null, item: null })
  const [quantidadeDigitada, setQuantidadeDigitada] = useState('')
  
  // Modal de rejeição
  const [rejectModal, setRejectModal] = useState({ open: false, pedidoId: null, item: null })
  const [motivoRejeicao, setMotivoRejeicao] = useState('')
  
  // Feedback de confirmação com desfazer
  const [confirmFeedback, setConfirmFeedback] = useState(null) // { pedidoId, itemId, timeoutId }
  
  // Modal "Confirmar Todos"
  const [confirmAllModal, setConfirmAllModal] = useState(false)

  const podeConfirmar = canConfirmCompras(perfil)

  const reload = useCallback(async () => {
    try {
      console.log('[ONLINE] Carregando compras pendentes do Supabase...')
      const lista = await fetchComprasPendentes()
      console.log('[ONLINE] Compras carregadas:', lista.length)
      // Garantir que itens seja sempre array válido
      const listaComItensValidos = lista.map(pedido => ({
        ...pedido,
        itens: Array.isArray(pedido.itens) ? pedido.itens : []
      }))
      setPedidos(listaComItensValidos)
    } catch (err) {
      console.error('[ONLINE] Erro ao carregar pedidos:', err)
      setPedidos([])
    }
  }, [])

  useEffect(() => {
    reload()
    // Atualiza a cada 10 segundos
    const interval = setInterval(reload, 10000)
    return () => clearInterval(interval)
  }, [reload])

  // Limpar feedback quando componente desmontar
  useEffect(() => {
    return () => {
      if (confirmFeedback?.timeoutId) {
        clearTimeout(confirmFeedback.timeoutId)
      }
    }
  }, [confirmFeedback])

  async function handleConfirmarItem() {
    if (!confirmModal.pedidoId || !confirmModal.item) return
    
    setBusy(true)
    try {
      const qtd = quantidadeDigitada.trim() === '' ? null : Number(quantidadeDigitada)
      
      const result = await confirmarCompra({
        pedidoId: confirmModal.pedidoId,
        itemId: confirmModal.item.id,
        quantidadeComprada: qtd,
        usuario: session
      })
      
      setConfirmModal({ open: false, pedidoId: null, item: null })
      setQuantidadeDigitada('')
      
      // Mostrar feedback com opção de desfazer
      const timeoutId = setTimeout(async () => {
        // Após 3 segundos, mover para histórico se todos os itens estiverem concluídos
        console.log('[Historico] Verificando status do pedido:', result.pedido.status)
        if (result.pedido.status !== 'aguardando') {
          console.log('[Historico] Movendo para histórico:', confirmModal.pedidoId)
          await moverParaHistoricoCompras(confirmModal.pedidoId)
        } else {
          console.log('[Historico] Pedido ainda aguardando, não move para histórico')
        }
        setConfirmFeedback(null)
        await reload()
      }, 3000)
      
      setConfirmFeedback({
        pedidoId: confirmModal.pedidoId,
        itemId: confirmModal.item.id,
        timeoutId,
        itemNome: confirmModal.item.produtoNome
      })

      // NOTIFICAÇÕES PUSH DESATIVADAS
      // notificarPedidoAprovado({
      //   empresaId: session?.empresaId,
      //   nomeAprovador: session?.usuarioNome || session?.nome || 'Comprador',
      //   numeroPedido: confirmModal.pedidoId?.slice(0, 8) || ''
      // }).catch(() => {})
      
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao confirmar compra.')
    } finally {
      setBusy(false)
    }
  }

  async function handleRejeitarItem() {
    if (!rejectModal.pedidoId || !rejectModal.item) return
    
    setBusy(true)
    try {
      await rejeitarItemCompra({
        pedidoId: rejectModal.pedidoId,
        itemId: rejectModal.item.id,
        motivo: motivoRejeicao,
        usuario: session
      })
      
      setRejectModal({ open: false, pedidoId: null, item: null })
      setMotivoRejeicao('')
      
      setToastType('success')
      setToast(`✔ Item "${rejectModal.item.produtoNome}" rejeitado e movido para o histórico de compras`)

      // NOTIFICAÇÕES PUSH DESATIVADAS
      // notificarPedidoRecusado({
      //   empresaId: session?.empresaId,
      //   nomeAprovador: session?.usuarioNome || session?.nome || 'Comprador',
      //   numeroPedido: rejectModal.pedidoId?.slice(0, 8) || '',
      //   motivo: motivoRejeicao || null
      // }).catch(() => {})
      
      await reload()
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao rejeitar item.')
    } finally {
      setBusy(false)
    }
  }

  async function handleDesfazer() {
    if (!confirmFeedback) return
    
    // Cancelar o timeout
    clearTimeout(confirmFeedback.timeoutId)
    setConfirmFeedback(null)
    
    // TODO: Implementar função de desfazer no orders.js
    // Por enquanto, apenas recarrega a lista
    setToastType('info')
    setToast('Ação desfeita.')
    await reload()
  }

  async function handleConfirmarTodos() {
    if (!Array.isArray(pedidos) || pedidos.length === 0) return
    
    setBusy(true)
    try {
      let confirmados = 0
      
      // Confirmar todos os itens pendentes de todos os pedidos
      for (const pedido of pedidos) {
        if (!Array.isArray(pedido?.itens)) continue
        for (const item of pedido.itens) {
          if (item?.status === 'aguardando') {
            await confirmarCompra({
              pedidoId: pedido.id,
              itemId: item.id,
              quantidadeComprada: null, // Quantidade total
              usuario: session
            })
            confirmados++
          }
        }
        // Mover para histórico
        await moverParaHistoricoCompras(pedido.id)
      }
      
      setConfirmAllModal(false)
      setToastType('success')
      setToast(`✔ Todos os pedidos confirmados (${confirmados} itens)`)
      
      // Feedback com desfazer
      const timeoutId = setTimeout(() => {
        setConfirmFeedback(null)
        reload()
      }, 3000)
      
      setConfirmFeedback({
        todos: true,
        timeoutId,
        quantidade: confirmados
      })
      
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao confirmar todos.')
    } finally {
      setBusy(false)
    }
  }

  // Agrupar itens pendentes de todos os pedidos
  const itensPendentes = (Array.isArray(pedidos) ? pedidos : []).flatMap(p => {
    const itens = Array.isArray(p?.itens) ? p.itens : []
    console.log('[ComprasPage] Pedido:', p?.numero, 'itens:', itens.length)
    console.log('[ComprasPage] Itens do pedido:', itens)
    const itensAguardando = itens.filter(i => i?.status === 'aguardando')
    console.log('[ComprasPage] Itens aguardando:', itensAguardando)
    return itensAguardando.map(i => ({ ...i, pedidoId: p?.id, pedidoNumero: p?.numero }))
  })

  console.log('[ComprasPage] Itens pendentes calculados:', itensPendentes)

  return (
    <div className="space-y-4">
      <Toast message={toast} type={toastType} onClose={() => setToast(null)} />
      
      <BackButton />

      <div className="rounded-2xl cm-card p-4">
        <div className="text-sm font-semibold">🛒 Compras</div>
        <div className="mt-1 text-xs text-white/70">
          {podeConfirmar ? 'Confirme os itens comprados' : 'Visualize os itens pendentes'}
        </div>
      </div>

      {/* Botão Confirmar Todos */}
      {podeConfirmar && itensPendentes.length > 0 && (
        <button
          onClick={() => setConfirmAllModal(true)}
          disabled={busy}
          className="w-full rounded-xl bg-accent-600 px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
        >
          ✓ Confirmar Todos ({itensPendentes.length} itens)
        </button>
      )}

      {/* Botão Histórico */}
      <button
        onClick={() => navigate('/app/historico-compras')}
        className="w-full rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold"
      >
        📋 Ver Histórico de Compras
      </button>

      {/* Feedback de Confirmação com Desfazer */}
      {confirmFeedback && (
        <div className="rounded-xl bg-emerald-500/20 border border-emerald-500/30 p-3">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              ✔ {confirmFeedback.todos 
                ? `${confirmFeedback.quantidade} pedidos confirmados` 
                : `${confirmFeedback.itemNome} confirmado`}
            </div>
            <button
              onClick={handleDesfazer}
              className="rounded bg-white/10 px-2 py-1 text-xs font-semibold"
            >
              Desfazer
            </button>
          </div>
          <div className="mt-1 text-[10px] text-white/60">
            Você tem 3 segundos para desfazer...
          </div>
        </div>
      )}

      {/* Estado Vazio */}
      {itensPendentes.length === 0 && !confirmFeedback && (
        <div className="rounded-2xl cm-card p-6 text-center">
          <div className="text-4xl mb-2">📭</div>
          <div className="text-sm text-white/80">Você não tem pedidos novos</div>
          <div className="mt-1 text-xs text-white/60">
            Quando o almoxarifado criar pedidos, eles aparecerão aqui.
          </div>
        </div>
      )}

      {/* Lista de Itens Pendentes */}
      {itensPendentes.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs font-semibold text-white/70">
            {itensPendentes.length} item(s) aguardando:
          </div>
          
          {itensPendentes.map((item, idx) => (
            <div 
              key={`${item?.pedidoId || idx}-${item?.id || idx}`} 
              className={`rounded-xl p-4 ${item?.urgencia === 'urgente' ? 'bg-red-500/20 border border-red-500/30' : 'bg-white/5'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold">{item?.produtoNome || 'Produto não informado'}</div>
                    {item?.modo === 'avulso' && (
                      <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] text-amber-300">Avulso</span>
                    )}
                    {item?.urgencia === 'urgente' && (
                      <span className="rounded bg-red-500/20 px-1.5 py-0.5 text-[10px] text-red-300">⚠ Urgente</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-white/70">
                    Pedido: {item?.pedidoNumero || 'N/A'}
                  </div>
                  <div className="mt-1 text-xs text-white/70">
                    Qtd solicitada: <span className="font-semibold text-white">{item?.quantidadeSolicitada || '—'}</span>
                    {item?.observacao && (
                      <span className="ml-2 text-white/50">• {item.observacao}</span>
                    )}
                  </div>
                </div>
                
                {podeConfirmar && (
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      onClick={() => setConfirmModal({ 
                        open: true, 
                        pedidoId: item?.pedidoId, 
                        item: item 
                      })}
                      disabled={busy}
                      className="rounded-xl bg-emerald-600/80 px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      Confirmar
                    </button>
                    <button
                      onClick={() => setRejectModal({ 
                        open: true, 
                        pedidoId: item?.pedidoId, 
                        item: item 
                      })}
                      disabled={busy}
                      className="rounded-xl bg-red-500/80 px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      Rejeitar
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Confirmação Individual */}
      <Modal
        open={confirmModal.open}
        title="Confirmar Compra"
        onClose={() => {
          setConfirmModal({ open: false, pedidoId: null, item: null })
          setQuantidadeDigitada('')
        }}
      >
        <div className="space-y-4">
          <div className="text-sm">
            <span className="font-semibold">{confirmModal.item?.produtoNome}</span>
          </div>
          <div className="text-xs text-white/70">
            Qtd solicitada: {confirmModal.item?.quantidadeSolicitada}
          </div>
          
          <div>
            <div className="text-xs text-white/70 mb-1">
              Quantidade comprada (opcional)
            </div>
            <input
              type="number"
              inputMode="numeric"
              value={quantidadeDigitada}
              onChange={(e) => setQuantidadeDigitada(e.target.value)}
              placeholder={`Máx: ${confirmModal.item?.quantidadeSolicitada}`}
              className="w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
            />
            <div className="mt-1 text-[10px] text-white/50">
              Deixe em branco para confirmar a quantidade total
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => {
                setConfirmModal({ open: false, pedidoId: null, item: null })
                setQuantidadeDigitada('')
              }}
              className="rounded-xl bg-white/10 px-3 py-3 text-sm font-semibold"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmarItem}
              disabled={busy}
              className="rounded-xl bg-emerald-600 px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? 'Confirmando…' : 'Confirmar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de Rejeição */}
      <Modal
        open={rejectModal.open}
        title="Rejeitar Item"
        onClose={() => {
          setRejectModal({ open: false, pedidoId: null, item: null })
          setMotivoRejeicao('')
        }}
      >
        <div className="space-y-4">
          <div className="text-sm">
            Rejeitar: <span className="font-semibold">{rejectModal.item?.produtoNome}</span>
          </div>
          <div className="text-xs text-white/70">
            O item será movido para o histórico de compras com status "rejeitado".
          </div>
          
          <div>
            <div className="text-xs text-white/70 mb-1">
              Motivo da rejeição (opcional)
            </div>
            <textarea
              value={motivoRejeicao}
              onChange={(e) => setMotivoRejeicao(e.target.value)}
              placeholder="Informe o motivo da rejeição..."
              className="w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none resize-none"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => {
                setRejectModal({ open: false, pedidoId: null, item: null })
                setMotivoRejeicao('')
              }}
              className="rounded-xl bg-white/10 px-3 py-3 text-sm font-semibold"
            >
              Cancelar
            </button>
            <button
              onClick={handleRejeitarItem}
              disabled={busy}
              className="rounded-xl bg-red-500 px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? 'Rejeitando…' : 'Rejeitar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Confirmar Todos */}
      <Modal
        open={confirmAllModal}
        title="Confirmar Todos os Pedidos"
        onClose={() => setConfirmAllModal(false)}
      >
        <div className="space-y-4">
          <div className="text-sm text-center">
            Deseja confirmar <span className="font-semibold">{itensPendentes.length} item(s)</span>?
          </div>
          <div className="text-xs text-white/70 text-center">
            Todos serão marcados como comprados (quantidade total).
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setConfirmAllModal(false)}
              className="rounded-xl bg-white/10 px-3 py-3 text-sm font-semibold"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmarTodos}
              disabled={busy}
              className="rounded-xl bg-emerald-600 px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? 'Confirmando…' : 'Sim, Confirmar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
